import React from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, ClockIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface ActionMonitoringDemoProps extends TeaserDemoProps {
  actions?: Array<{
    id: string;
    type: 'PROMOTE' | 'DEMOTE' | 'SWAP';
    strategy: string;
    status: 'PENDING' | 'EXECUTING' | 'COMPLETED' | 'FAILED';
    progress: number;
    timestamp: string;
  }>;
}

const ActionMonitoringDemoComponent: React.FC<ActionMonitoringDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  actions = [
    { id: '1', type: 'PROMOTE', strategy: 'Elite Scalper', status: 'EXECUTING', progress: 75, timestamp: '14:32:15' },
    { id: '2', type: 'DEMOTE', strategy: 'Beta Trader', status: 'COMPLETED', progress: 100, timestamp: '14:28:42' },
    { id: '3', type: 'SWAP', strategy: 'Grid Master', status: 'PENDING', progress: 0, timestamp: '14:35:01' }
  ]
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return ClockIcon;
      case 'EXECUTING': return PlayIcon;
      case 'COMPLETED': return CheckCircleIcon;
      case 'FAILED': return XMarkIcon;
      default: return ClockIcon;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'text-yellow-400';
      case 'EXECUTING': return 'text-blue-400';
      case 'COMPLETED': return 'text-green-400';
      case 'FAILED': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getBgColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-500/20 border-yellow-500/30';
      case 'EXECUTING': return 'bg-blue-500/20 border-blue-500/30';
      case 'COMPLETED': return 'bg-green-500/20 border-green-500/30';
      case 'FAILED': return 'bg-red-500/20 border-red-500/30';
      default: return 'bg-gray-500/20 border-gray-500/30';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            boxShadow: [
              '0 0 10px rgba(59, 130, 246, 0.3)',
              '0 0 20px rgba(59, 130, 246, 0.6)',
              '0 0 10px rgba(59, 130, 246, 0.3)'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center"
        >
          <PlayIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Action Monitor</h4>
          <p className="text-gray-300 text-xs">{actions.length} active operations</p>
        </div>
      </div>

      {/* Action Items */}
      <div className="space-y-2 flex-1">
        {actions.map((action, index) => {
          const StatusIcon = getStatusIcon(action.status);
          return (
            <motion.div
              key={action.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.2, duration: 0.5 }}
              className={`p-3 rounded-lg border backdrop-blur-sm ${getBgColor(action.status)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <StatusIcon className={`w-4 h-4 ${getStatusColor(action.status)}`} />
                  <span className="text-white text-sm font-medium">
                    {action.type} {action.strategy}
                  </span>
                </div>
                <span className="text-xs text-gray-400">{action.timestamp}</span>
              </div>

              {/* Progress Bar */}
              {action.status === 'EXECUTING' ? (
                <div className="w-full bg-black/20 rounded-full h-1.5 mb-1">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${action.progress}%` }}
                    transition={{ duration: 2, ease: "easeInOut" }}
                    className="h-full bg-gradient-to-r from-blue-400 to-green-400 rounded-full"
                  />
                </div>
              ) : (
                <div className="w-full bg-black/20 rounded-full h-1.5 mb-1">
                  <div 
                    className={`h-full rounded-full ${
                      action.status === 'COMPLETED' 
                        ? 'bg-green-400' 
                        : action.status === 'FAILED'
                        ? 'bg-red-400'
                        : 'bg-yellow-400/50'
                    }`}
                    style={{ width: `${action.progress}%` }}
                  />
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className={`text-xs font-medium ${getStatusColor(action.status)}`}>
                  {action.status}
                </span>
                <span className="text-xs text-gray-400">{action.progress}%</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Real-time Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="mt-3 text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-green-300 text-xs font-medium flex items-center justify-center gap-1"
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-green-400 rounded-full"
          />
          Real-time execution tracking
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const ActionMonitoringDemo = createOptimizedTeaserDemo(
  ActionMonitoringDemoComponent,
  'ActionMonitoringDemo'
);